import { createBrowserRouter } from "react-router";

import { ShellLoadingFallback } from "../loading/ShellLoadingFallback";
import { createLoadingSlot, type LoadingSlot } from "../loading/loading-slot";

import { createShellLoader } from "./loader";
import { ShellErrorBoundary } from "./ShellErrorBoundary";
import { ShellRoute } from "./ShellRoute";
import { SHELL_ROUTE_ID } from "./use-shell-loader-data";
import type { ShellConfig } from "./types";

/**
 * Creates the React Router instance for the Tinkertoy shell.
 *
 * A single catch-all route delegates all routing decisions to the server.
 * The loader fetches view data for whatever path the browser is on;
 * React Router handles navigation detection, history, back/forward,
 * and scroll restoration.
 *
 * The loader receives `viewLoader` and `moduleLoader` so it can prime CDN
 * imports from an early streaming assets phase; components still read the same
 * loaders from `InternalTinkertoyProvider`.
 *
 * The loader also writes streamed loading metadata into `loadingSlot`; pass
 * the same slot to `InternalTinkertoyProvider` so the first-load fallback and
 * `TinkertoyShell` can render the flow's loading module.
 */
export function createShellRouter(
  config: ShellConfig,
  loadingSlot: LoadingSlot = createLoadingSlot(),
) {
  return createBrowserRouter([
    {
      id: SHELL_ROUTE_ID,
      path: "*",
      loader: createShellLoader({
        apiBaseUrl: config.apiBaseUrl,
        viewLoader: config.viewLoader,
        moduleLoader: config.moduleLoader,
        loadingSlot,
      }),
      element: <ShellRoute />,
      hydrateFallbackElement: <ShellLoadingFallback />,
      errorElement: <ShellErrorBoundary />,
    },
  ]);
}
